import { FC, useState } from 'react'
import { CircularProgress, IconButton, Typography } from '@mui/material'
import toast from 'react-hot-toast'
import { ThemeButton } from 'ui/components/buttons'
import { HotToast } from 'ui/components/toast'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ExclusionRulesServiceV4 } from 'services'
import { ModalActions, ModalBody, ModalHeader, ModalRoot } from './deleteExclusionRule.styles'
import { showExclusionRuleDeletedToast } from './showExclusionRuleDeletedToast'

const CloseIcon: FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 18L18 6M6 6L18 18"
      stroke="#D0C3CC"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DeleteExclusionRule: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const props = modalProps.props?.deleteExclusionRuleProps
  const [submitting, setSubmitting] = useState(false)

  const handleClose = () => {
    if (submitting) return
    dispatch(closeModal())
  }

  const handleConfirm = async () => {
    if (!props || props.ruleIds.length === 0) return
    setSubmitting(true)
    try {
      await ExclusionRulesServiceV4.deleteRules(props.ruleIds)
      showExclusionRuleDeletedToast()
      props.onSuccess?.()
      dispatch(closeModal())
    } catch (e) {
      console.error(e)
      toast.error(() => <HotToast text="Failed to delete exclusion rule. Please try again." />)
    } finally {
      setSubmitting(false)
    }
  }

  if (!props) return null

  const isBulk = props.ruleIds.length > 1
  const headline = isBulk
    ? 'Delete Exclusion Rules?'
    : props.ruleName
    ? `Delete Exclusion Rule “${props.ruleName}”?`
    : 'Delete Exclusion Rule?'
  const description = isBulk
    ? 'Are you sure you want to delete these exclusion rules?\nRecords matching these rules will be restored to the main list and will be included in calculations again.'
    : 'Are you sure you want to delete this exclusion rule?\nRecords matching this rule will be restored to the main list and will be included in calculations again.'

  return (
    <ModalRoot>
      <ModalHeader>
        <Typography className="title">{headline}</Typography>
        <IconButton className="close-button" onClick={handleClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </ModalHeader>

      <ModalBody>
        <Typography className="description">{description}</Typography>
      </ModalBody>

      <ModalActions>
        <ThemeButton
          themevariant="secondary"
          shape="rectangular"
          type="button"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </ThemeButton>
        <ThemeButton
          themevariant="primary"
          shape="rectangular"
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          endIcon={submitting && <CircularProgress size="20px" />}
        >
          Delete
        </ThemeButton>
      </ModalActions>
    </ModalRoot>
  )
}

export default DeleteExclusionRule
